import * as React from 'react';
import { createRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Box, Flex, Text } from '@opendoor/bricks/core';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';

import {
  SellExclusivesCategory_ReferringApp,
  SellExclusivesCategory_SellerPreference,
} from 'src/__generated__/protobuf/journey/data/categories/sell_exclusives_category';
import Layout from 'src/components/exclusives/layouts/ExclusivesLayout';
import PrivacyNotice from 'src/components/flowPage/PrivacyNotice';
import opendoorSellingOptions from 'src/helpers/flows/pages/exclusives/sellerInterestPage';
import { useMetricsContext } from 'src/helpers/MetricsContext';
import { useObservability } from 'src/helpers/observability';
import { isOpendoorAppEvoWebView } from 'src/helpers/webview';
import { getQuestionKeys } from 'src/lib/flow';
import { EvaluatedPageFields } from 'src/lib/flow/FlowPage';
import { FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  DO_CURRENT_PAGE_LATER,
  TO_MANUAL_ADDRESS_VERIFICATION,
} from 'src/store/sellerFlow/actionTypes';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';
import { SavedAnswer } from 'src/store/sellerInput';

import FlowQuestions from '../../../containers/sellerFlow/FlowQuestions';
import { showPrompt } from '../../../helpers/showPrompt';
import { isAgent } from '../../../lib/flow/pageHelpers';
import { getCurrentSlug, getInApp, getLeadToken } from '../../../store/router/selectors';
import {
  address as getAddress,
  getAnswers,
  changedAnswers as getChangedAnswers,
  channel as getChannel,
  leadState as getLeadState,
  market as getMarket,
  getPreviousPath,
  progressPercentage as getProgressPercentage,
} from '../../../store/sellerFlow/selectors';
import AddressBar from '../../sellerFlow/AddressBar';
import NavigationControl from '../components/ExclusivesNavigationControl';
import Header from '../layouts/ExclusivesHeader';
import ExclusivesDelay from '../pages/ExclusivesDelay';

const getShowSkip = (
  currentSlug: string,
  currentPage: Readonly<EvaluatedPageFields> | undefined,
  answers: AnswerSet,
  changedAnswers: Record<string, SavedAnswer | null>,
): boolean => {
  if (currentSlug === 'overview') {
    // Do not show the 'Skip' button on /overview page
    return false;
  }
  if (!currentPage || !currentPage?.questions?.length) {
    return false;
  }

  // Get all required questions for the current page
  const requiredQuestionKeys = getQuestionKeys(
    currentPage.questions,
    (q: FlowQuestion) => !q.hidden && (q.type === 'element' || !q.optional),
  );

  // Check if we already have answers for the current page
  const answerKeys = [...Object.keys(answers)];
  const currentPageHasAnswers = getQuestionKeys(currentPage.questions).some((x) =>
    answerKeys.includes(x),
  );

  return (
    requiredQuestionKeys.length === 0 &&
    Object.keys(changedAnswers ?? {}).length === 0 &&
    !currentPageHasAnswers
  );
};

const QuestionsPage: React.FC = () => {
  const titleRef = createRef<HTMLHeadingElement>();
  const currentSlug = useAppSelector(getCurrentSlug);
  const answers = useAppSelector(getAnswers);
  const progressPercentage = useAppSelector(getProgressPercentage);
  const changedAnswers = useAppSelector(getChangedAnswers);
  const currentPage = useAppSelector((state) => state.ui.page);
  const inApp = useAppSelector(getInApp);

  const { trackJourneyEvent } = useObservability();
  const hideProgressBar = currentPage?.hideProgress;
  const title = currentPage?.title ?? '';
  const subtitle = currentPage?.subtitle ?? '';
  const showPrivacyNotice = currentPage?.showPrivacyNotice ?? '';
  const note = currentPage?.note ?? '';
  const tooltip = currentPage?.tooltip ?? '';
  const address = useAppSelector(getAddress);
  const isAddressAutoVerified = useAppSelector((state) => state.sellerInput.isAddressAutoVerified);
  const showEditAddress = !!(currentSlug === 'relationship' && isAddressAutoVerified);
  const channel = useAppSelector(getChannel);
  const showOrva = !isAgent(answers, channel);
  const pageError = useAppSelector((state) => state.ui.pageError);
  const canAdvance = useAppSelector((state) => state.ui.canAdvance);
  const atEnd = useAppSelector((state) => state.flow_ng.atEnd);
  const sellerInputId = useSelector(getLeadToken) as string;
  const initialSellerInterest = answers['seller.exclusives_initial_seller_options_interest'];
  const sellDirectSelection = initialSellerInterest && initialSellerInterest['sell_direct'];
  const exclusivesSelection = initialSellerInterest && initialSellerInterest['exclusives'];
  const traditionalSelection =
    initialSellerInterest && initialSellerInterest['traditional_listing'];
  const currentSlugIsExclusivesPhonePage = currentSlug === 'exclusives-phone';
  const showSkip = getShowSkip(currentSlug, currentPage, answers, changedAnswers);
  let nextButtonText = currentSlugIsExclusivesPhonePage ? 'View Offer' : showSkip ? 'Skip' : 'Next';
  if (currentPage?.nextText) {
    nextButtonText = currentPage?.nextText;
  }

  const previousPath = useAppSelector(getPreviousPath);

  const appDispatch = useAppDispatch();

  useEffect(() => {
    if (!atEnd) {
      // The listener 'beforeunload' cannot differentiate between close and
      // refresh. Thus, do not show prompt on the last slug, otherwise prompt
      // will show before redirect to cake
      window.addEventListener('beforeunload', showPrompt);
    }
  }, [atEnd]);

  useEffect(() => {
    titleRef.current?.focus();
    // eslint-disable-next-line
  }, [currentPage?.title]);

  const onNext = useCallback(() => {
    if (currentSlug === opendoorSellingOptions.slug()) {
      trackJourneyEvent('sellExclusives', 'sellerPreferencesSelectionEvent', {
        sellerInputId,
        referrer: SellExclusivesCategory_ReferringApp.RECEPTION,
        sellerPreferences: [
          ...(sellDirectSelection ? [SellExclusivesCategory_SellerPreference.SELL_DIRECT] : []),
          ...(exclusivesSelection ? [SellExclusivesCategory_SellerPreference.EXCLUSIVES] : []),
          ...(traditionalSelection ? [SellExclusivesCategory_SellerPreference.TRADITIONAL] : []),
        ],
      });
    }
  }, [
    currentSlug,
    exclusivesSelection,
    sellDirectSelection,
    sellerInputId,
    trackJourneyEvent,
    traditionalSelection,
  ]);

  if (currentSlug === 'exclusives-final') {
    return <ExclusivesDelay />;
  }

  return (
    <Layout
      hideFooter={inApp}
      progressPercentage={progressPercentage}
      hideProgressBar={hideProgressBar}
      currentSlug={currentSlug}
      renderNavigationControl={() => (
        <NavigationControl
          pageError={pageError}
          previousPath={previousPath}
          onDoThisLater={() => appDispatch({ type: DO_CURRENT_PAGE_LATER })}
          canAdvance={canAdvance}
          nextButtonText={nextButtonText}
          isStickyOnMobile
          currentSlug={currentSlug}
          onNext={onNext}
        />
      )}
    >
      <Box pt={[0, 0, 10]}>
        {showOrva ||
          (currentSlug === 'schedule' && (
            <Text
              id="calculating-prelim-offer"
              padding={6}
              mb={6}
              borderRadius="roundedSquare"
              backgroundColor="neutrals10"
              color="neutrals100"
              fontWeight="bold"
              fontSize="s0"
            >
              We're calculating your preliminary offer now. You'll be able to see it in your
              dashboard in just a moment.
            </Text>
          ))}
        {showEditAddress && address && (
          <Flex mt="8" flexDirection="column" alignItems={['flex-start', 'center']} mb={6}>
            <Box mb={3}>
              <img
                src="https://imgdrop.imgix.net/7a2a8ecf0b0544578a8f96c97ebe9fec.png"
                alt="verify home icon"
              />
            </Box>
            <AddressBar
              address={address}
              goToManualAddressVerificationPage={() => {
                appDispatch({ type: TO_MANUAL_ADDRESS_VERIFICATION });
              }}
            />
          </Flex>
        )}
        <Header
          title={title}
          subtitle={subtitle}
          titleRef={titleRef}
          currentSlug={currentSlug}
          tooltip={tooltip}
        />
        <FlowQuestions />
        {showPrivacyNotice && <PrivacyNotice />}
        {note && (
          <Text mt={6} color="neutrals90" fontSize="s00" textAlign={['left', null, 'center']}>
            {note}
          </Text>
        )}
      </Box>
    </Layout>
  );
};

const ThemedQuestionsPage: React.FC = () => {
  const { updateContextualOptions } = useMetricsContext();
  const channel = useAppSelector(getChannel);
  const leadState = useAppSelector(getLeadState);
  const leadToken = useAppSelector(getLeadToken);
  const market = useAppSelector(getMarket);
  const inApp = useAppSelector(getInApp);
  const currentSlug = useAppSelector(getCurrentSlug);

  const isExclusivesFinalPage = currentSlug === 'exclusives-final';

  React.useEffect(() => {
    updateContextualOptions({
      channel: channel,
      leadState: leadState,
      leadToken: leadToken,
      market: market,
      inApp: inApp,
      isMobileNativeWebView: isOpendoorAppEvoWebView,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadToken]);

  return (
    <ThemedLite theme={isExclusivesFinalPage ? 'legacy' : 'novo'}>
      <QuestionsPage />
    </ThemedLite>
  );
};

export default ThemedQuestionsPage;
